import React from 'react';

const Button = ({ 
  onClick,
  disabled = false,
  isLoading = false,
  variant = 'primary', // primary, secondary, danger, success, warning, orange, teal, yellow, offer
  size = 'md',         // sm, md, lg
  icon,                // optional icon component
  children,
  className = '',
  type = 'button',
  fullWidth = false,
  style
}) => {
  const isDisabled = disabled || isLoading;
  
  const baseClasses = 'inline-flex items-center justify-center border font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  // Disabled styling - completely different from enabled variants
  const disabledClasses = 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed opacity-60 shadow-none';

  // Active variant classes - only applied when not disabled
  const variantClasses = {
    primary: 'text-white bg-brand-teal border-brand-teal hover:bg-brand-teal-dark focus:ring-brand-teal',
    secondary: 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 focus:ring-brand-teal',
    danger: 'text-white bg-red-600 border-red-600 hover:bg-red-700 focus:ring-red-500',
    'danger-outline': 'text-red-600 bg-white border-red-300 hover:bg-red-50 focus:ring-red-500',
    success: 'text-white bg-green-500 border-green-500 hover:bg-green-600 focus:ring-green-500',
    'success-outline': 'text-green-600 bg-white border-green-300 hover:bg-green-50 focus:ring-green-500',
    warning: 'text-white bg-yellow-500 border-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500',
    orange: 'text-white bg-brand-orange border-brand-orange hover:bg-brand-orange-light focus:ring-brand-orange',
    teal: 'text-white bg-brand-teal border-brand-teal hover:bg-brand-teal/90 focus:ring-brand-teal',
    'teal-outline': 'text-brand-teal bg-white border-brand-teal hover:bg-brand-teal/5 focus:ring-brand-teal',
    yellow: 'text-yellow-800 bg-yellow-100 border-yellow-100 hover:bg-yellow-200 focus:ring-yellow-200',
    offer: 'text-gray-800 bg-brand-beige-light border-brand-beige-light hover:bg-brand-beige focus:ring-brand-beige',
    outline: 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50 focus:ring-brand-teal'
  };

  const widthClass = fullWidth ? 'w-full' : '';

  // Use conditional classes instead of pseudo-class overrides
  const appliedVariantClasses = isDisabled ? disabledClasses : variantClasses[variant];

  // Helper function to render icon properly
  const renderIcon = () => {
    if (!icon) return null;
    
    // If icon is a React component (function), render it with props
    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent className="h-5 w-5" />;
    }
    
    // If icon is already a React element, render it directly
    return icon;
  };

  return (
    <button
      type={type}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`${baseClasses} ${sizeClasses[size]} ${appliedVariantClasses} ${widthClass} ${className}`}
      style={style}
    >
      {isLoading ? (
        <div className="h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          {icon && <span className="mr-1.5">{renderIcon()}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default Button; 